import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { BusinessBoStateValue, BusinessDateValue, BusinessDivisionValue } from "@src/components/business-data-display";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import {
    actOfCheckingAvailabilityServices,
    type ActOfCheckingAvailability,
    type ActOfCheckingAvailabilityExtends,
    type ActOfCheckingAvailabilityFilter,
} from "../../../../api/actOfCheckingAvailability";
import { stockPartialInventoryListWindows } from "./stockPartialInventoryList.windows";
import "./StockPartialInventoryList.scss";

const PARTIAL_INVENTORY_LIST_INCLUDES = {
    include: ["Division", "State"] satisfies Array<keyof ActOfCheckingAvailabilityExtends>,
};

/** Список АПН для частичной инвентаризации склада. */
export const StockPartialInventoryList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<ActOfCheckingAvailabilityFilter>();

    const list = useBusinessListQuery<ActOfCheckingAvailability, ActOfCheckingAvailabilityFilter>({
        queryKey: "stock.partialInventory.acts",
        filter: filterState.filter,
        queryFn: (filter, signal) =>
            actOfCheckingAvailabilityServices.load(
                {
                    filter,
                    ...PARTIAL_INVENTORY_LIST_INCLUDES,
                },
                { signal },
            ),
        getId: (item) => item.Code,
    });

    const openFilterWindow = (): void => {
        stockPartialInventoryListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (filter) => {
                filterState.applyFilter(filter, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    return (
        <div className="stock_partial_inventory_list_page">
            <CardHeader
                text="Частичная инвентаризация склада"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`АПН №${item.Num}`}
                        props={[
                            { label: "Статус АПН", value: <BusinessBoStateValue state={item.State} /> },
                            { label: "Склад", value: <BusinessDivisionValue division={item.Division} /> },
                            { label: "Дата", value: <BusinessDateValue value={item.Date} /> },
                        ]}
                        onClick={() => {
                            navigate({ pathname: `${item.Code}` });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
